// An episode is a list of shots. Each shot is one generated picture with the characters painted into it (from
// reference pictures: library assets and earlier shots), talking faces placed on it by hand, and the lines said over
// it. Only the faces move (talking, blinking, feelings) and the whole picture wiggles, unless the shot has motion.
import type { Cue, Emotion, FaceSpots } from "./face";

export const EPISODE_VERSION = 4;

export type Picture = { src: string; w: number; h: number };

// One picture ever made for a shot or an asset: drawn from a description, or changed from another picture. Shots and
// assets add their own fields (a shot's references, an asset's face).
export type PictureVersion = {
  id: string;
  picture: Picture;
  how: "drawn" | "changed";
  text: string; // the description it was drawn from, or what was asked to change
  at: number;
};

// Where the camera points, as fractions of the frame, and how far in. zoom 1 shows the whole picture.
export type Camera = { cx: number; cy: number; zoom: number };

// Something a shot is painted from: a library asset (character, place or prop) or an earlier shot.
export type ShotRef = { kind: "asset"; id: string } | { kind: "shot"; id: string };

// A character's talking face on a picture. Spots are in that picture's pixels. `tilt`: degrees clockwise, each part
// turned around its own centre (the spots are already where the tilted face puts them).
export type ShotFace = FaceSpots & { character: string; tilt?: number };

// A picture a shot has had, with the references it was painted from.
export type ShotVersion = PictureVersion & { refs: ShotRef[] };

export type Line = {
  id: string;
  speaker: string; // a character's asset id, with a face in the shot
  emotion: Emotion; // how they feel at the start
  camera: Camera;
  status: "syncing" | "ready" | "error";
  error?: string;
  audio?: string; // file name inside the episode folder
  duration: number; // seconds, after silence trimming
  cues: Cue[];
  text?: string; // typed lines: what the AI voice said
  direction?: string; // typed lines: the note the voice was given, like "whisper it"
  takes?: Take[]; // every take this line has had, oldest first; the fields above are the one in use. Old lines have none.
};

// One take of a line: its sound and mouth (the same fields the line holds for the take in use). Each has its own audio file.
export type Take = Pick<Line, "audio" | "duration" | "cues" | "text" | "emotion" | "direction"> & { id: string; at: number; audio: string };

export type Shot = {
  id: string;
  name: string; // what references and descriptions call it
  refs: ShotRef[];
  prompt: string;
  picture?: Picture;
  versions?: ShotVersion[]; // every picture made, oldest first; old shots have none (the current picture is the only one)
  faces: ShotFace[]; // back to front
  wiggle: number; // boil strength over the whole picture, 0 = still
  motions?: Motion[]; // every animation made for this shot, oldest first, kept to choose between
  motionId?: string; // the one in the film, which plays from the start of the shot's first line; none = the still
  lines: Line[];
};

// How a shot's animation is made (docs/shot-motion.md):
//   "grid"     one image-model picture holding 9 frames in a 3x3 grid, cut apart: stop motion in the shot's own style
//   "video"    a short video from the shot (MiniMax H3 Max Turbo), its frames used as they are
//   "repaint"  the same video for the movement, then a handful of its frames repainted by the image model
export type MotionMethod = "grid" | "video" | "repaint";

// One frame of an animation. Spots are in the frame's own pixels. `found`: faces the magenta dots gave; `placed`:
// faces moved by hand, which win. A character with neither in a frame follows along between the frames that do.
export type MotionFrame = Picture & { found?: ShotFace[]; placed?: ShotFace[] };

export type Motion = {
  id: string;
  made: number; // when, ms since 1970
  model?: string; // the image model that painted it (src/model/imageModels.ts); none for a plain video
  method: MotionMethod;
  prompt: string; // what happens
  end?: Picture; // a painted last frame the animation heads for, if one was made
  dots: boolean; // faces follow the characters using the dots; off = only frames placed by hand move them
  seconds?: number; // video length asked for ("video" and "repaint")
  count?: number; // frames repainted ("repaint")
  video?: { src: string; frames: MotionFrame[] }; // "repaint": the rough video and its frames, kept to repaint again
  frames: MotionFrame[]; // frame 0 is always the shot's own picture, with the shot's own faces
  fps: number; // playback speed
  loop: boolean; // made to loop: the last frame leads back into the first. false = play once and hold the last frame
};

// The animation a shot plays in the film, if any.
export const shotMotion = (shot: Shot): Motion | undefined => shot.motions?.find(m => m.id === shot.motionId);

export type Episode = {
  version: number;
  id: string;
  title: string;
  shots: Shot[];
  createdAt: number;
  updatedAt: number;
};

export type EpisodeSummary = Pick<Episode, "id" | "title" | "updatedAt"> & {
  lines: number;
  duration: number;
  picture?: string; // the first shot's picture
  cast: string[];
  demo?: true; // a visitor's demo episode
};

export const allLines = (ep: Episode) => ep.shots.flatMap(s => s.lines);
